import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Lock, Camera, Loader2, Trash2, ArrowRight, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { userAPI, uploadAPI, mediaUrl } from '../services/api';

const Slot = ({ label, day, url, consent, onUpload, onClear, uploading }) => {
  const ref = useRef(null);
  return (
    <div className="flex-1">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-red-700/80 dark:text-red-400">{label}</p>
          <p className="text-[11px] text-muted-foreground">{day}</p>
        </div>
        {url && (
          <button onClick={onClear} data-testid={`clear-${label.toLowerCase()}`} className="text-muted-foreground/60 hover:text-red-600">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <button
        onClick={() => consent && ref.current?.click()}
        disabled={!consent || uploading}
        data-testid={`slot-${label.toLowerCase()}`}
        className={`w-full aspect-[3/4] rounded-2xl border-2 border-dashed flex items-center justify-center overflow-hidden transition-all ${
          url ? 'border-transparent' : 'border-red-200 dark:border-red-900/50 hover:bg-red-50/50 dark:hover:bg-red-950/30'
        } ${!consent ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {url ? (
          <img src={mediaUrl(url)} alt={label} className="w-full h-full object-cover" />
        ) : uploading ? (
          <Loader2 className="w-6 h-6 animate-spin text-red-600" />
        ) : (
          <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
            <Camera className="w-7 h-7" />
            <span className="text-xs font-medium">Ajouter</span>
          </div>
        )}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid={`input-${label.toLowerCase()}`} />
    </div>
  );
};

export default function PhotosAvantApres() {
  const [photos, setPhotos] = useState({ before: null, after: null, photo_consent: false });
  const [loading, setLoading] = useState(true);
  const [uploadingSlot, setUploadingSlot] = useState(null);

  useEffect(() => {
    userAPI.photos().then(setPhotos).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const setConsent = async (v) => {
    setPhotos((p) => ({ ...p, photo_consent: v }));
    try { await userAPI.updatePhotos({ photo_consent: v }); }
    catch { toast.error('Échec de la mise à jour'); }
  };

  const upload = (slot) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingSlot(slot);
    try {
      const res = await uploadAPI.upload(file);
      const updated = await userAPI.updatePhotos({ [slot]: res.url });
      setPhotos(updated);
      toast.success('Photo enregistrée (privée)');
    } catch { toast.error("Échec de l'envoi"); }
    finally { setUploadingSlot(null); e.target.value = ''; }
  };

  const clear = (slot) => async () => {
    const updated = await userAPI.updatePhotos({ [slot]: '' });
    setPhotos(updated);
    toast.success('Photo supprimée');
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-600" /></div>;

  return (
    <div className="space-y-6 animate-rise max-w-lg mx-auto">
      <div>
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-red-950 dark:text-red-50">📸 Avant / Après</h1>
        <p className="text-muted-foreground text-sm mt-1">Ton espace privé pour voir ta transformation entre le jour 1 et le jour 21.</p>
      </div>

      {/* Privacy + consent */}
      <Card className="p-4 rounded-2xl border-red-100 dark:border-red-900/50 bg-red-50/60 dark:bg-red-950/30" data-testid="photos-consent-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-600 text-white flex items-center justify-center shrink-0">
            <Lock className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-red-950 dark:text-red-50">Photos 100 % privées 🔒</p>
            <p className="text-xs text-muted-foreground mt-0.5">Tes photos ne sont visibles que par toi. Elles ne sont jamais publiées dans la communauté.</p>
          </div>
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-red-100 dark:border-red-900/50">
          <div className="pr-3">
            <p className="text-sm font-semibold text-foreground">Je consens à téléverser mes photos</p>
            <p className="text-xs text-muted-foreground">Stockage privé et sécurisé, supprimable à tout moment.</p>
          </div>
          <Switch checked={!!photos.photo_consent} onCheckedChange={setConsent} data-testid="photos-consent-switch" />
        </div>
      </Card>

      {/* Comparison */}
      <Card className="p-4 rounded-2xl border-red-100 dark:border-red-900/50">
        <div className="flex items-center gap-3">
          <Slot
            label="Avant" day="Jour 1"
            url={photos.before} consent={!!photos.photo_consent}
            onUpload={upload('before')} onClear={clear('before')}
            uploading={uploadingSlot === 'before'}
          />
          <ArrowRight className="w-5 h-5 text-red-500 shrink-0" />
          <Slot
            label="Après" day="Jour 21"
            url={photos.after} consent={!!photos.photo_consent}
            onUpload={upload('after')} onClear={clear('after')}
            uploading={uploadingSlot === 'after'}
          />
        </div>
        {!photos.photo_consent && (
          <p className="text-xs text-center text-muted-foreground mt-4 flex items-center justify-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" /> Active le consentement ci-dessus pour ajouter tes photos.
          </p>
        )}
        {photos.before && photos.after && (
          <div className="mt-4 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900/40 p-3 text-center" data-testid="transformation-done">
            <p className="text-sm font-semibold text-green-700 dark:text-green-400">Bravo pour ta transformation ! 💪</p>
          </div>
        )}
      </Card>
    </div>
  );
}
